interface AsaasConfig {
  apiKey: string;
  baseURL: string;
  environment: "sandbox" | "production";
}

interface Cliente {
  name: string;
  email: string;
  cpfCnpj: string;
  phone: string;
  postalCode: string;
  address: string;
  addressNumber: string;
  complement?: string;
  province: string;
  city: string;
  state: string;
}

interface Plano {
  id: string;
  nome: string;
  preco: number;
  descricao: string;
  funcionalidades: string[];
}

interface Cobranca {
  customer: string;
  billingType: "PIX" | "CREDIT_CARD" | "BOLETO";
  value: number;
  dueDate: string;
  description: string;
  externalReference: string;
}

interface Assinatura {
  customer: string;
  billingType: "PIX" | "CREDIT_CARD";
  value: number;
  nextDueDate: string;
  cycle: "MONTHLY";
  description: string;
  externalReference: string;
}

class AsaasService {
  private config: AsaasConfig;

  /**
   * Inicializa o serviço do Asaas com base no ambiente atual.
   * Detecta o host para definir o ambiente (sandbox/produção) e
   * monta a URL base do proxy no backend.
   */
  constructor() {
    // Detectar ambiente baseado na URL atual
    const currentHost = window.location.hostname;
    const isHomolog = currentHost.includes("cipa-homolog.ehsfacil.com");

    // API URL do backend (proxy)
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";

    this.config = {
      // API Key não é mais necessária no frontend - o backend gerencia as chaves
      apiKey: "", // Removido - backend proxy gerencia autenticação
      baseURL: `${apiUrl}/api/asaas`, // Usar proxy do backend
      environment: isHomolog ? "sandbox" : "production",
    };

    console.log(
      `🔧 [ASAAS-SERVICE] Ambiente detectado: ${this.config.environment} (${currentHost})`,
    );
    console.log(`🔧 [ASAAS-SERVICE] API URL: ${this.config.baseURL}`);
  }

  /**
   * Cria um cliente no Asaas via proxy do backend.
   * @param dadosCliente - Dados cadastrais do cliente.
   * @returns Objeto retornado pela API do Asaas.
   * @throws Erro quando a API retornar falha.
   */
  async criarCliente(dadosCliente: Cliente) {
    try {
      const response = await fetch(`${this.config.baseURL}/v3/customers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Environment": this.config.environment, // Enviar ambiente para o backend
        },
        body: JSON.stringify(dadosCliente),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.errors?.[0]?.description || "Erro ao criar cliente",
        );
      }

      return result;
    } catch (error) {
      console.error("❌ [ASAAS] Erro ao criar cliente:", error);
      throw error;
    }
  }

  /**
   * Cria uma cobrança única para o cliente e plano informados.
   * @param clienteId - ID do cliente no Asaas.
   * @param plano - Plano escolhido com preço e descrição.
   * @param metodoPagamento - Método de pagamento (PIX ou cartão).
   * @returns Dados da cobrança criada.
   * @throws Erro quando a API retornar falha.
   */
  async criarCobranca(
    clienteId: string,
    plano: Plano,
    metodoPagamento: "PIX" | "CREDIT_CARD",
  ) {
    try {
      const cobranca: Cobranca = {
        customer: clienteId,
        billingType: metodoPagamento,
        value: plano.preco,
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        description: `Assinatura ${plano.nome} - CIPA Fácil EHS`,
        externalReference: `plano-${plano.id}-${Date.now()}`,
      };

      const response = await fetch(`${this.config.baseURL}/v3/payments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Environment": this.config.environment,
        },
        body: JSON.stringify(cobranca),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.errors?.[0]?.description || "Erro ao criar cobrança",
        );
      }

      return result;
    } catch (error) {
      console.error("❌ [ASAAS] Erro ao criar cobrança:", error);
      throw error;
    }
  }

  /**
   * Processa pagamento imediato com cartão de crédito.
   * @param clienteId - ID do cliente no Asaas.
   * @param plano - Plano escolhido.
   * @param dadosCartao - Dados do cartão e titular.
   * @returns Dados do pagamento confirmado/recusado.
   * @throws Erro com detalhes retornados pelo Asaas.
   */
  async processarPagamentoCartao(
    clienteId: string,
    plano: Plano,
    dadosCartao: any,
  ) {
    try {
      const pagamento: any = {
        customer: clienteId,
        billingType: "CREDIT_CARD",
        value: plano.preco,
        dueDate: new Date().toISOString().split("T")[0], // Hoje
        description: `Pagamento ${plano.nome} - CIPA Fácil EHS`,
        externalReference: `pagamento-${plano.id}-${Date.now()}`,
        creditCard: {
          holderName: dadosCartao.holderName,
          number: dadosCartao.number.replace(/\s/g, ""),
          expiryMonth: dadosCartao.expiryMonth,
          expiryYear: dadosCartao.expiryYear,
          ccv: dadosCartao.cvv,
        },
        creditCardHolderInfo: {
          name: dadosCartao.holderName,
          email: dadosCartao.email || "",
          cpfCnpj: dadosCartao.cpfCnpj || "",
          postalCode: dadosCartao.postalCode || "",
          addressNumber: dadosCartao.addressNumber || "",
          phone: dadosCartao.phone || "",
        },
      };

      const response = await fetch(`${this.config.baseURL}/v3/payments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Environment": this.config.environment,
        },
        body: JSON.stringify(pagamento),
      });

      const result = await response.json();

      if (!response.ok) {
        // Preservar a estrutura de erros do Asaas
        const error = new Error("Erro ao processar pagamento");
        (error as any).errors = result.errors || [];
        (error as any).response = { data: result };
        throw error;
      }

      return result;
    } catch (error) {
      console.error("❌ [ASAAS] Erro ao processar pagamento:", error);
      throw error;
    }
  }

  /**
   * Cria uma assinatura recorrente (mensal) para o cliente.
   * @param clienteId - ID do cliente no Asaas.
   * @param plano - Plano escolhido.
   * @param metodoPagamento - Método de pagamento (PIX ou cartão).
   * @returns Dados da assinatura criada.
   * @throws Erro quando a API retornar falha ou timeout.
   */
  async criarAssinatura(
    clienteId: string,
    plano: Plano,
    metodoPagamento: "PIX" | "CREDIT_CARD",
  ) {
    try {
      const assinatura: Assinatura = {
        customer: clienteId,
        billingType: metodoPagamento,
        value: plano.preco,
        nextDueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        cycle: "MONTHLY",
        description: `Assinatura ${plano.nome} - CIPA Fácil EHS`,
        externalReference: `assinatura-${plano.id}-${Date.now()}`,
      };
      // Criar AbortController para timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 segundos

      const response = await fetch(`${this.config.baseURL}/v3/subscriptions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Environment": this.config.environment,
        },
        body: JSON.stringify(assinatura),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const result = await response.json();

      if (!response.ok) {
        console.error("❌ [ASAAS] Erro na API:", result);
        throw new Error(
          result.errors?.[0]?.description || "Erro ao criar assinatura",
        );
      }

      return result;
    } catch (error) {
      console.error("❌ [ASAAS] Erro ao criar assinatura:", error);

      if (error instanceof Error && error.name === "AbortError") {
        throw new Error(
          "Timeout: A requisição demorou mais de 30 segundos para responder",
        );
      }

      throw error;
    }
  }

  /**
   * Obtém o QR Code PIX associado a uma cobrança.
   * @param paymentId - ID do pagamento no Asaas.
   * @returns Dados do QR Code PIX.
   * @throws Erro quando a API retornar falha.
   */
  async obterQrCodePix(paymentId: string) {
    try {
      const response = await fetch(
        `${this.config.baseURL}/v3/payments/${paymentId}/pixQrCode`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            "X-Environment": this.config.environment,
          },
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.errors?.[0]?.description || "Erro ao obter QR Code PIX",
        );
      }

      return result;
    } catch (error) {
      console.error("❌ [ASAAS] Erro ao obter QR Code PIX:", error);
      throw error;
    }
  }

  /**
   * Consulta o status de um pagamento.
   * @param paymentId - ID do pagamento no Asaas.
   * @returns Dados atualizados do pagamento.
   * @throws Erro quando a API retornar falha.
   */
  async consultarPagamento(paymentId: string) {
    try {
      const response = await fetch(
        `${this.config.baseURL}/v3/payments/${paymentId}`,
        {
          method: "GET",
          headers: {
            "X-Environment": this.config.environment,
          },
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.errors?.[0]?.description || "Erro ao consultar pagamento",
        );
      }

      return result;
    } catch (error) {
      console.error("❌ [ASAAS] Erro ao consultar pagamento:", error);
      throw error;
    }
  }

  /**
   * Consulta o status de uma assinatura.
   * @param subscriptionId - ID da assinatura no Asaas.
   * @returns Dados atualizados da assinatura.
   * @throws Erro quando a API retornar falha.
   */
  async consultarAssinatura(subscriptionId: string) {
    try {
      const response = await fetch(
        `${this.config.baseURL}/v3/subscriptions/${subscriptionId}`,
        {
          method: "GET",
          headers: {
            "X-Environment": this.config.environment,
          },
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.errors?.[0]?.description || "Erro ao consultar assinatura",
        );
      }

      return result;
    } catch (error) {
      console.error("❌ [ASAAS] Erro ao consultar assinatura:", error);
      throw error;
    }
  }

  /**
   * Retorna a configuração atual do serviço.
   * @returns Configuração com ambiente e baseURL do proxy.
   */
  getConfig() {
    return this.config;
  }
}

export default new AsaasService();
export type { Cliente, Plano, Cobranca, Assinatura };
